// 📄 pages/Soporte/TicketConversacion.tsx
// Conversación dentro de un ticket de soporte (D5 de
// docs/plans/plan-diagnostico-documentos.md). La usan "Ayuda" (el creador,
// miRol="usuario") y SoporteHome (Admin, miRol="soporte"). Plegada por
// defecto: se suscribe al hilo solo cuando se abre.
import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, Send, RefreshCw } from 'lucide-react';
import { Ticket, TicketMensaje, escucharMensajes, enviarMensajeTicket } from '../../services/ticketService.ts';

const fecha = (ts: any) => (ts?.toDate ? ts.toDate().toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '');

export default function TicketConversacion({ ticket, miRol }: { ticket: Ticket; miRol: 'usuario' | 'soporte' }) {
  const [abierta, setAbierta] = useState(false);
  const [mensajes, setMensajes] = useState<TicketMensaje[] | null>(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const finRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierta) return;
    return escucharMensajes(ticket.id, setMensajes, (err) => {
      console.error('Error leyendo la conversación:', err);
      setError('No se pudo cargar la conversación.');
    });
  }, [abierta, ticket.id]);

  useEffect(() => {
    finRef.current?.scrollIntoView({ block: 'nearest' });
  }, [mensajes]);

  const cerrado = ticket.estado === 'Cerrado';
  // La otra parte escribió último: hay algo que leer.
  const pendiente = !!ticket.ultimoMensajePor && ticket.ultimoMensajePor !== miRol;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!texto.trim() || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await enviarMensajeTicket(ticket.id, texto.trim());
      setTexto('');
    } catch (err: any) {
      setError(err.message || 'No se pudo enviar el mensaje.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => setAbierta((a) => !a)}
        className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300"
      >
        <MessageSquare className="w-3.5 h-3.5" />
        {abierta ? 'Ocultar conversación' : 'Conversación'}
        {pendiente && !abierta && (
          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/20">
            {miRol === 'usuario' ? 'Soporte respondió' : 'El usuario respondió'}
          </span>
        )}
      </button>

      {abierta && (
        <div className="mt-2 bg-black/20 border border-white/10 rounded-lg p-3 space-y-3">
          {mensajes === null && !error ? (
            <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
          ) : mensajes && mensajes.length === 0 ? (
            <p className="text-xs text-gray-500">Todavía no hay mensajes.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {(mensajes || []).map((m) => {
                const mio = m.autorRol === miRol;
                return (
                  <div key={m.id} className={`flex ${mio ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${mio ? 'bg-blue-600/20 border border-blue-500/30' : 'bg-white/5 border border-white/10'}`}>
                      <p className="text-[11px] text-gray-400 mb-0.5">
                        {m.autorNombre || (m.autorRol === 'soporte' ? 'Soporte' : 'Usuario')}
                        {m.autorRol === 'soporte' ? ' · Soporte' : ''} · {fecha(m.creadoEn)}
                      </p>
                      <p className="text-gray-200 whitespace-pre-wrap break-words">{m.texto}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={finRef} />
            </div>
          )}

          {error && <p className="text-xs text-red-400">{error}</p>}

          {cerrado ? (
            <p className="text-xs text-gray-500">El ticket está cerrado: no admite mensajes nuevos.</p>
          ) : (
            <form onSubmit={enviar} className="flex gap-2 items-end">
              <textarea
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) enviar(e);
                }}
                rows={2}
                maxLength={2000}
                placeholder={miRol === 'usuario' ? 'Escribe a soporte…' : 'Responde al usuario…'}
                className="flex-1 bg-zinc-800 border border-white/10 rounded-lg p-2 text-sm text-white focus:outline-none focus:border-blue-500"
              />
              <button
                type="submit"
                disabled={enviando || !texto.trim()}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-2 rounded-lg text-sm font-semibold text-white"
              >
                {enviando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
